import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  ArrowRight, MapPin, Star, Tent, Sparkles, Flame, Moon, Coffee,
  BedDouble, UtensilsCrossed, Compass, Check, Loader2,
  X, ChevronLeft, ChevronRight,
} from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { HOTELS } from "@/lib/data";
import { getBivouacDetail } from "@/lib/bivouacDetails";
import VideoSection from "@/components/VideoSection";
import BrandMonogramEdge from "@/components/BrandMonogramEdge";
import PageHero from "@/components/PageHero";
import MapSection from "@/components/MapSection";
import PageStickyNav from "@/components/PageStickyNav";
import { Tent as TentIcon, Sparkles as SparkIcon, Compass as CompassIcon, MapPin as MapPinIcon, Mail as MailIcon } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Curated list of bivouac slugs to feature on this dedicated page.
const FEATURED_BIVOUAC_SLUGS = [
  "la-belle-etoile-bivouacs",
  "kamkamia-luxury-desert-camp",
  "merdani-bivouac",
];

const HERO_IMAGES = [
  "https://images.unsplash.com/photo-1473580044384-7ba9967e16a0?auto=format&fit=crop&w=2400&q=80",
  "https://images.unsplash.com/photo-1518684079-3c830dcef090?auto=format&fit=crop&w=2400&q=80",
  "https://customer-assets.emergentagent.com/job_xaluca-luxury/artifacts/htd6p1fu_Kasbah%20Hotel%20Xaluca%20-%20Ae%CC%81reas%20-%20HamidBabni-4.jpg",
];

const pick = (obj, lang) => {
  if (!obj) return "";
  if (typeof obj === "string") return obj;
  return obj[lang] || obj.en || obj.fr || "";
};

const COPY = {
  overline: { en: "Bivouacs & Desert Camps", fr: "Bivouacs & Camps du désert", es: "Vivacs y Campamentos del desierto" },
  title: {
    en: "A night in the Sahara",
    fr: "Une nuit dans le Sahara",
    es: "Una noche en el Sáhara",
  },
  sub: {
    en: "From camel-hair haimas to white luxury suite tents — three ways to disappear into the silence of the Erg Chebbi.",
    fr: "Des haimas en poil de chameau aux tentes-suites blanches de luxe — trois façons de disparaître dans le silence de l'Erg Chebbi.",
    es: "De haimas de pelo de camello a tiendas-suite blancas de lujo — tres formas de desaparecer en el silencio del Erg Chebbi.",
  },
  intro_overline: { en: "The Sahara, up close", fr: "Le Sahara, de tout près", es: "El Sáhara, de cerca" },
  intro_title: {
    en: "What is a desert bivouac?",
    fr: "Qu'est-ce qu'un bivouac du désert ?",
    es: "¿Qué es un vivac del desierto?",
  },
  intro_text: {
    en: "A bivouac is the oldest form of Saharan hospitality — a temporary camp pitched directly on the sand, dismantled at the end of the season so the dunes are left as they were found. Around Erg Chebbi you will encounter three distinct kinds, each with its own character. Choosing between them is mostly a question of how you'd like to feel when the sun finally drops behind the dunes.",
    fr: "Le bivouac est la plus ancienne forme d'hospitalité saharienne — un campement temporaire monté à même le sable, démonté en fin de saison pour rendre aux dunes leur état d'origine. Autour de l'Erg Chebbi, vous croiserez trois formes distinctes, chacune avec son caractère. Choisir entre elles tient surtout à la manière dont vous voulez vous sentir au moment où le soleil bascule derrière les dunes.",
    es: "El vivac es la forma más antigua de hospitalidad sahariana — un campamento temporal montado directamente sobre la arena, desmontado al final de la temporada para devolver las dunas a su estado original. Alrededor del Erg Chebbi encontrará tres formas distintas, cada una con su carácter. Elegir entre ellas depende sobre todo de cómo desea sentirse cuando el sol finalmente se hunde tras las dunas.",
  },
  type_traditional: {
    title: { en: "Traditional camel-skin haimas", fr: "Haimas traditionnelles en poil de chameau", es: "Haimas tradicionales de pelo de camello" },
    text: {
      en: "Dark, low tents woven from camel and goat hair — the same weave the Sahrawi nomads have used for a thousand years. They breathe in summer, hold heat in winter, and feel like a held breath when the wind rises.",
      fr: "Tentes basses et sombres tissées en poil de chameau et de chèvre — la même trame utilisée par les nomades sahraouis depuis mille ans. Elles respirent l'été, retiennent la chaleur l'hiver, et se taisent comme un souffle retenu quand le vent se lève.",
      es: "Tiendas bajas y oscuras tejidas con pelo de camello y de cabra — la misma trama que han usado los nómadas sahrauis durante mil años. Respiran en verano, retienen el calor en invierno y se aquietan como un aliento contenido cuando se levanta el viento.",
    },
  },
  type_luxury: {
    title: { en: "White luxury suite tents", fr: "Tentes-suites blanches de luxe", es: "Tiendas-suite blancas de lujo" },
    text: {
      en: "Tall white canvas suites with en-suite bathrooms, king beds, copper basins and wooden floors. The same comfort you'd expect from a five-star hotel — but with a zip for a door and the Milky Way for a ceiling.",
      fr: "Hautes suites en toile blanche avec salles de bain en suite, lits king, vasques en cuivre et planchers en bois. Le confort d'un cinq étoiles — avec une fermeture éclair pour porte et la Voie lactée pour plafond.",
      es: "Suites altas de lona blanca con baños en suite, camas king, lavabos de cobre y suelos de madera. El confort de un cinco estrellas — con una cremallera por puerta y la Vía Láctea como techo.",
    },
  },
  type_stars: {
    title: { en: "Sleeping under the stars", fr: "Dormir à la belle étoile", es: "Dormir bajo las estrellas" },
    text: {
      en: "On clear nights, a mattress on the sand outside your tent, a heavy wool blanket and the most honest hotel ceiling on earth. The Sahara sky is so dark that the Milky Way casts a faint shadow on the dunes.",
      fr: "Les nuits claires, un matelas posé à même le sable devant votre tente, une lourde couverture en laine et le plafond d'hôtel le plus honnête du monde. Le ciel saharien est si sombre que la Voie lactée projette une ombre légère sur les dunes.",
      es: "En las noches despejadas, un colchón sobre la arena frente a su tienda, una pesada manta de lana y el techo de hotel más sincero del mundo. El cielo sahariano es tan oscuro que la Vía Láctea proyecta una sombra tenue sobre las dunas.",
    },
  },
  atmosphere_title: {
    en: "What it actually feels like",
    fr: "Ce que l'on ressent vraiment",
    es: "Lo que realmente se siente",
  },
  atmosphere_text: {
    en: "It is, first of all, the silence. A silence so wide that you can hear your own pulse, then a fire crackling, then the muffled bell of a returning camel. The light goes through copper, bronze and rose before the first star appears. After dinner, a Sahrawi musician plays the qraqeb; an astronomer points to Orion; the tea is poured from a great height and tastes of mint and dust. You go to bed late. You wake before sunrise — not from an alarm, but because the desert tells you to.",
    fr: "C'est d'abord le silence. Un silence si vaste que l'on entend son propre pouls, puis le crépitement d'un feu, puis la cloche feutrée d'un chameau qui rentre. La lumière passe du cuivre au bronze, puis au rose, avant qu'apparaisse la première étoile. Après le dîner, un musicien sahraoui joue du qraqeb ; un astronome pointe Orion ; le thé est versé de très haut et a goût de menthe et de poussière. Vous vous couchez tard. Vous vous éveillez avant l'aube — pas à cause d'un réveil, mais parce que le désert vous le demande.",
    es: "Es, ante todo, el silencio. Un silencio tan amplio que se oye el propio pulso, después una hoguera crepitando, después la campana sorda de un camello que regresa. La luz pasa por el cobre, el bronce y el rosa antes de que aparezca la primera estrella. Tras la cena, un músico sahraui toca el qraqeb; un astrónomo apunta a Orión; el té se sirve desde muy alto y sabe a menta y a polvo. Se acuesta tarde. Se despierta antes del amanecer — no por una alarma, sino porque el desierto se lo pide.",
  },
  discover_more: { en: "Discover more", fr: "En savoir plus", es: "Descubrir más" },
  request_info: { en: "Request information", fr: "Demander des informations", es: "Solicitar información" },
  close_form: { en: "Close form", fr: "Fermer le formulaire", es: "Cerrar formulario" },
  highlights: { en: "Key highlights", fr: "Temps forts", es: "Aspectos destacados" },
  accommodation: { en: "Accommodation", fr: "Hébergement", es: "Alojamiento" },
  activities: { en: "Included experiences", fr: "Expériences incluses", es: "Experiencias incluidas" },
  dining: { en: "Dining & atmosphere", fr: "Restauration & ambiance", es: "Gastronomía y ambiente" },
  short_label: { en: "At a glance", fr: "En un coup d'œil", es: "De un vistazo" },
};

const ATMOSPHERE_BEATS = [
  { icon: Moon, en: "Silence so wide you can hear your pulse", fr: "Un silence si vaste qu'on entend son pouls", es: "Un silencio tan amplio que se oye el pulso" },
  { icon: Flame, en: "Campfires that last until the stars set", fr: "Des feux de camp qui durent jusqu'au coucher des étoiles", es: "Hogueras que duran hasta que se ponen las estrellas" },
  { icon: Sparkles, en: "Sunsets that turn the sand to bronze", fr: "Des couchers de soleil qui changent le sable en bronze", es: "Atardeceres que vuelven la arena bronce" },
  { icon: Compass, en: "Nomadic inspiration in every detail", fr: "L'inspiration nomade dans chaque détail", es: "Inspiración nómada en cada detalle" },
  { icon: Star, en: "Astronomy with Saharan guides", fr: "Astronomie avec guides sahariens", es: "Astronomía con guías saharianos" },
  { icon: Coffee, en: "The slow ceremony of mint tea", fr: "La cérémonie lente du thé à la menthe", es: "La ceremonia lenta del té con menta" },
];

const FormLabels = {
  full_name: { en: "Full name", fr: "Nom complet", es: "Nombre completo" },
  email: { en: "Email", fr: "Email", es: "Correo electrónico" },
  phone: { en: "Phone (optional)", fr: "Téléphone (optionnel)", es: "Teléfono (opcional)" },
  preferred_date: { en: "Preferred date", fr: "Date souhaitée", es: "Fecha preferida" },
  guests: { en: "Guests", fr: "Voyageurs", es: "Huéspedes" },
  notes: { en: "Anything we should know?", fr: "Quelque chose à savoir ?", es: "¿Algo que debamos saber?" },
  submit: { en: "Send request", fr: "Envoyer la demande", es: "Enviar solicitud" },
  sending: { en: "Sending…", fr: "Envoi…", es: "Enviando…" },
  thanks: {
    en: "Request received — our concierge will reply within 24h.",
    fr: "Demande reçue — notre conciergerie vous répond sous 24h.",
    es: "Solicitud recibida — nuestro conserje le responderá en 24h.",
  },
  error: {
    en: "Something went wrong. Please try again or write to xaluca@xaluca.com.",
    fr: "Une erreur est survenue. Réessayez ou écrivez à xaluca@xaluca.com.",
    es: "Algo salió mal. Inténtelo o escriba a xaluca@xaluca.com.",
  },
};

const BivouacRequestForm = ({ bivouac, lang, onClose }) => {
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", preferred_date: "", guests: 2, notes: "" });
  const [status, setStatus] = useState({ submitting: false, success: false, error: false });

  const submit = async (e) => {
    e.preventDefault();
    if (status.submitting) return;
    setStatus({ submitting: true, success: false, error: false });
    try {
      await axios.post(`${API}/experience-requests`, {
        full_name: form.full_name,
        email: form.email,
        phone: form.phone || null,
        experience_key: bivouac.slug,
        experience_title: bivouac.name,
        preferred_date: form.preferred_date || null,
        guests: Number(form.guests) || 2,
        notes: form.notes || null,
        language: lang,
        source: "bivouac_page",
      });
      setStatus({ submitting: false, success: true, error: false });
      setForm({ full_name: "", email: "", phone: "", preferred_date: "", guests: 2, notes: "" });
    } catch {
      setStatus({ submitting: false, success: false, error: true });
    }
  };

  if (status.success) {
    return (
      <div className="bg-[#1A1513] text-[#FDFBF7] border border-[#D4A373]/40 p-6 md:p-8" data-testid={`bivouac-form-success-${bivouac.slug}`}>
        <p className="overline !text-[#D4A373] flex items-center gap-2">
          <Check className="w-3.5 h-3.5" strokeWidth={2.2} />
          {pick({ en: "Thank you", fr: "Merci", es: "Gracias" }, lang)}
        </p>
        <p className="font-serif-x mt-3 text-xl md:text-2xl leading-snug">
          {pick(FormLabels.thanks, lang)}
        </p>
        <button
          type="button"
          onClick={onClose}
          className="mt-6 inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-[#D4A373] hover:text-[#FDFBF7]"
          data-testid={`bivouac-form-close-${bivouac.slug}`}
        >
          {pick(COPY.close_form, lang)}
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      className="bg-[#F2EBE1]/60 border border-[#2C2621]/10 p-6 md:p-8"
      data-testid={`bivouac-form-${bivouac.slug}`}
      noValidate
    >
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <p className="overline">{pick(COPY.request_info, lang)}</p>
          <p className="font-serif-x text-xl text-[#2C2621] mt-1">{bivouac.name}</p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-xs tracking-[0.25em] uppercase text-[#5C5248] hover:text-[#C16542]"
          data-testid={`bivouac-form-cancel-${bivouac.slug}`}
        >
          ×
        </button>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <label className="block">
          <span className="overline">{pick(FormLabels.full_name, lang)} *</span>
          <input
            type="text"
            required
            value={form.full_name}
            onChange={(e) => setForm({ ...form, full_name: e.target.value })}
            className="mt-2 w-full bg-transparent border-b border-[#2C2621]/25 focus:border-[#C16542] py-2 outline-none text-base"
            data-testid={`bivouac-form-name-${bivouac.slug}`}
          />
        </label>
        <label className="block">
          <span className="overline">{pick(FormLabels.email, lang)} *</span>
          <input
            type="email"
            required
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="mt-2 w-full bg-transparent border-b border-[#2C2621]/25 focus:border-[#C16542] py-2 outline-none text-base"
            data-testid={`bivouac-form-email-${bivouac.slug}`}
          />
        </label>
        <label className="block">
          <span className="overline">{pick(FormLabels.phone, lang)}</span>
          <input
            type="tel"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            className="mt-2 w-full bg-transparent border-b border-[#2C2621]/25 focus:border-[#C16542] py-2 outline-none text-base"
            data-testid={`bivouac-form-phone-${bivouac.slug}`}
          />
        </label>
        <label className="block">
          <span className="overline">{pick(FormLabels.guests, lang)}</span>
          <input
            type="number"
            min="1"
            max="20"
            value={form.guests}
            onChange={(e) => setForm({ ...form, guests: e.target.value })}
            className="mt-2 w-full bg-transparent border-b border-[#2C2621]/25 focus:border-[#C16542] py-2 outline-none text-base"
            data-testid={`bivouac-form-guests-${bivouac.slug}`}
          />
        </label>
        <label className="block sm:col-span-2">
          <span className="overline">{pick(FormLabels.preferred_date, lang)}</span>
          <input
            type="date"
            value={form.preferred_date}
            onChange={(e) => setForm({ ...form, preferred_date: e.target.value })}
            className="mt-2 w-full bg-transparent border-b border-[#2C2621]/25 focus:border-[#C16542] py-2 outline-none text-base"
            data-testid={`bivouac-form-date-${bivouac.slug}`}
          />
        </label>
        <label className="block sm:col-span-2">
          <span className="overline">{pick(FormLabels.notes, lang)}</span>
          <textarea
            rows={2}
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            className="mt-2 w-full bg-transparent border border-[#2C2621]/20 focus:border-[#C16542] p-2.5 outline-none text-base resize-none"
            data-testid={`bivouac-form-notes-${bivouac.slug}`}
          />
        </label>
      </div>
      <div className="mt-5 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3">
        <p className="text-xs text-[#5C5248]">
          {pick({
            en: "We typically reply within 24 hours.",
            fr: "Nous répondons généralement sous 24 heures.",
            es: "Solemos responder en 24 horas.",
          }, lang)}
        </p>
        <button
          type="submit"
          disabled={status.submitting}
          className="bg-[#C16542] hover:bg-[#A35133] disabled:opacity-60 text-[#FDFBF7] px-6 py-3 text-xs tracking-[0.25em] uppercase inline-flex items-center justify-center gap-2"
          data-testid={`bivouac-form-submit-${bivouac.slug}`}
        >
          {status.submitting ? (
            <>
              <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.5} />
              {pick(FormLabels.sending, lang)}
            </>
          ) : (
            <>
              {pick(FormLabels.submit, lang)}
              <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
            </>
          )}
        </button>
      </div>
      {status.error && (
        <p className="mt-4 text-sm text-[#A35133] border-t border-[#A35133]/30 pt-3" data-testid={`bivouac-form-error-${bivouac.slug}`}>
          {pick(FormLabels.error, lang)}
        </p>
      )}
    </form>
  );
};

const BivouacLightbox = ({ images, title, startIndex = 0, onClose }) => {
  const [idx, setIdx] = useState(startIndex);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") setIdx((i) => (i + 1) % images.length);
      if (e.key === "ArrowLeft") setIdx((i) => (i - 1 + images.length) % images.length);
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [images.length, onClose]);

  const prev = () => setIdx((i) => (i - 1 + images.length) % images.length);
  const next = () => setIdx((i) => (i + 1) % images.length);

  return (
    <div
      className="fixed inset-0 z-[100] bg-[#1A1513]/95 flex items-center justify-center px-4 md:px-12"
      data-testid="bivouac-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={onClose}
    >
      <button
        onClick={onClose}
        className="absolute top-6 right-6 text-[#FDFBF7]/80 hover:text-[#FDFBF7] p-2"
        data-testid="bivouac-lightbox-close"
        aria-label="Close gallery"
      >
        <X className="w-6 h-6" strokeWidth={1.5} />
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); prev(); }}
        className="absolute left-2 md:left-8 top-1/2 -translate-y-1/2 text-[#FDFBF7]/70 hover:text-[#FDFBF7] p-3"
        data-testid="bivouac-lightbox-prev"
        aria-label="Previous image"
      >
        <ChevronLeft className="w-8 h-8" strokeWidth={1.5} />
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); next(); }}
        className="absolute right-2 md:right-8 top-1/2 -translate-y-1/2 text-[#FDFBF7]/70 hover:text-[#FDFBF7] p-3"
        data-testid="bivouac-lightbox-next"
        aria-label="Next image"
      >
        <ChevronRight className="w-8 h-8" strokeWidth={1.5} />
      </button>
      <div className="max-w-6xl w-full" onClick={(e) => e.stopPropagation()}>
        <img
          src={images[idx]}
          alt={`${title} ${idx + 1}`}
          className="w-full max-h-[78vh] object-contain"
          data-testid="bivouac-lightbox-image" loading="lazy" decoding="async"/>
        <div className="mt-6 flex items-center justify-between text-[#FDFBF7]/85">
          <p className="font-serif-x text-xl md:text-2xl">{title}</p>
          <p className="text-xs tracking-[0.25em] uppercase tabular-nums">
            {String(idx + 1).padStart(2, "0")} / {String(images.length).padStart(2, "0")}
          </p>
        </div>
        <div className="mt-4 flex gap-2 overflow-x-auto pb-1">
          {images.map((src, i) => (
            <button
              key={`thumb-${i}`}
              onClick={() => setIdx(i)}
              className={`shrink-0 w-20 h-14 overflow-hidden border-2 transition-opacity ${
                i === idx ? "border-[#D4A373] opacity-100" : "border-transparent opacity-60 hover:opacity-100"
              }`}
              data-testid={`bivouac-lightbox-thumb-${i}`}
              aria-label={`Go to image ${i + 1}`}
            >
              <img src={src} alt="" className="w-full h-full object-cover" loading="lazy" decoding="async"/>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

const BivouacCard = ({ bivouac, index, lang }) => {
  const detail = getBivouacDetail(bivouac.slug);
  const [openForm, setOpenForm] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const formRef = useRef(null);

  // Build the full gallery: main image first, then unique gallery images.
  const galleryImages = React.useMemo(() => {
    const list = [bivouac.image, ...(bivouac.gallery || [])];
    return Array.from(new Set(list.filter(Boolean)));
  }, [bivouac.image, bivouac.gallery]);

  const openLightbox = (i) => setLightboxIndex(i);
  const closeLightbox = () => setLightboxIndex(null);

  const handleRequestClick = () => {
    setOpenForm(true);
    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      formRef.current?.querySelector("input")?.focus?.();
    }, 200);
  };

  return (
    <article className="border-t border-[#2C2621]/10 pt-14 md:pt-20" data-testid={`bivouac-card-${bivouac.slug}`} data-cms-scope={`bivouac-card.${bivouac.slug}`}>
      <div className={`grid md:grid-cols-12 gap-8 md:gap-12 items-start ${index % 2 === 1 ? "md:[&>*:first-child]:order-2" : ""}`}>
        <div className="md:col-span-7">
          <button
            type="button"
            onClick={() => openLightbox(0)}
            className="group block relative overflow-hidden aspect-[16/10] w-full text-left"
            data-testid={`bivouac-image-${bivouac.slug}`}
            aria-label={`Open ${bivouac.name} gallery`}
          >
            <img src={bivouac.image} alt={bivouac.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" decoding="async" data-cms-role="hero"/>
            <BrandMonogramEdge
              size="md"
              opacity={0.45}
              testIdPrefix={`bivouac-card-${bivouac.slug}-brand`}
            />
          </button>
          {bivouac.gallery && bivouac.gallery.length > 1 && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
              {bivouac.gallery.slice(0, 4).map((g, gi) => {
                const targetIdx = galleryImages.indexOf(g);
                return (
                  <button
                    type="button"
                    key={`g-${gi}`}
                    onClick={() => openLightbox(targetIdx >= 0 ? targetIdx : 0)}
                    className="relative overflow-hidden aspect-[4/3] group/thumb"
                    data-testid={`bivouac-thumb-${bivouac.slug}-${gi}`}
                    aria-label={`Open image ${gi + 1} of ${bivouac.name}`}
                  >
                    <img src={g} alt={`${bivouac.name} ${gi + 1}`} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover/thumb:scale-105" loading="lazy" decoding="async" data-cms-role={`thumb.${gi}`}/>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="md:col-span-5">
          <p className="overline flex items-center gap-2">
            <span className="text-[#C16542] tabular-nums">{String(index + 1).padStart(2, "0")}</span>
            <span className="text-[#5C5248]/60">·</span>
            <MapPin className="w-3 h-3 text-[#C16542]" strokeWidth={1.5} /> {bivouac.location}
          </p>
          <h3 className="font-serif-x text-3xl md:text-4xl lg:text-5xl mt-3 leading-[1.05] tracking-tight">
            <Link to={`/hotels/${bivouac.slug}`} className="hover:text-[#C16542] transition-colors">{bivouac.name}</Link>
          </h3>
          <p className="mt-2 text-sm tracking-[0.18em] uppercase text-[#D4A373]">
            {lang === "fr" ? bivouac.region_fr : lang === "es" ? bivouac.region_es : bivouac.region}
          </p>
          {detail && (
            <p className="mt-4 font-serif-x italic text-lg text-[#5C5248]">{pick(detail.tagline, lang)}</p>
          )}

          <p className="mt-5 text-base text-[#5C5248] leading-relaxed">
            {pick(detail?.short || bivouac.intro, lang)}
          </p>

          <div className="mt-7">
            <p className="overline">{pick(COPY.highlights, lang)}</p>
            <ul className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-sm text-[#2C2621]">
              {bivouac.amenities.slice(0, 4).map((a, idx) => (
                <li key={`am-${idx}`} className="flex items-start gap-2">
                  <Star className="w-3 h-3 text-[#C16542] mt-1 shrink-0" strokeWidth={2} />
                  <span>{a}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-4">
            <Link
              to={`/hotels/${bivouac.slug}`}
              className="bg-[#C16542] hover:bg-[#A35133] text-[#FDFBF7] px-6 py-3 text-xs tracking-[0.25em] uppercase inline-flex items-center gap-2"
              data-testid={`bivouac-details-${bivouac.slug}`}
            >
              {pick(COPY.discover_more, lang)}
              <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
            </Link>
            <button
              type="button"
              onClick={handleRequestClick}
              className="border border-[#2C2621]/30 hover:border-[#C16542] text-[#2C2621] hover:text-[#C16542] px-6 py-3 text-xs tracking-[0.25em] uppercase inline-flex items-center gap-2 transition-colors"
              data-testid={`bivouac-request-${bivouac.slug}`}
            >
              {pick(COPY.request_info, lang)}
            </button>
          </div>
        </div>
      </div>

      {detail && (
        <div className="grid md:grid-cols-12 gap-8 md:gap-12 mt-12 md:mt-14">
          <div className="md:col-span-7">
            <p className="overline">{pick(COPY.short_label, lang)}</p>
            <p className="mt-4 text-lg text-[#5C5248] leading-relaxed">{pick(detail.long, lang)}</p>
          </div>
          <div className="md:col-span-5 grid grid-cols-1 gap-6">
            <div>
              <p className="overline flex items-center gap-2"><BedDouble className="w-3.5 h-3.5 text-[#C16542]" strokeWidth={1.5} /> {pick(COPY.accommodation, lang)}</p>
              <p className="mt-2 text-sm text-[#2C2621] leading-relaxed">{pick(detail.accommodation, lang)}</p>
            </div>
            <div>
              <p className="overline flex items-center gap-2"><UtensilsCrossed className="w-3.5 h-3.5 text-[#C16542]" strokeWidth={1.5} /> {pick(COPY.dining, lang)}</p>
              <p className="mt-2 text-sm text-[#2C2621] leading-relaxed">{pick(detail.dining, lang)}</p>
            </div>
            <div>
              <p className="overline flex items-center gap-2"><Compass className="w-3.5 h-3.5 text-[#C16542]" strokeWidth={1.5} /> {pick(COPY.activities, lang)}</p>
              <ul className="mt-3 space-y-1.5 text-sm text-[#2C2621]">
                {detail.activities.map((a, ai) => (
                  <li key={`a-${ai}`} className="flex items-start gap-2">
                    <Check className="w-3 h-3 text-[#C16542] mt-1 shrink-0" strokeWidth={2.2} />
                    <span>{pick(a, lang)}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {openForm && (
        <div ref={formRef} className="mt-10">
          <BivouacRequestForm bivouac={bivouac} lang={lang} onClose={() => setOpenForm(false)} />
        </div>
      )}

      {lightboxIndex !== null && (
        <BivouacLightbox
          images={galleryImages}
          title={bivouac.name}
          startIndex={lightboxIndex}
          onClose={closeLightbox}
        />
      )}
    </article>
  );
};

const BivouacsPage = () => {
  const { lang } = useLanguage();
  const bivouacs = FEATURED_BIVOUAC_SLUGS
    .map((slug) => HOTELS.find((h) => h.slug === slug))
    .filter(Boolean);

  return (
    <div data-testid="page-bivouacs" className="bg-[#FDFBF7]">
      {/* HERO — multi-slide (video first, then bivouac photography) */}
      <section data-testid="bivouacs-hero">
        <PageHero
          images={HERO_IMAGES}
          alt="Bivouacs · Grup Xaluca"
          scope="page.bivouacs"
          overline={pick(COPY.overline, lang)}
          title={pick(COPY.title, lang)}
          sub={pick(COPY.sub, lang)}
        />
      </section>

      {/* Interactive map — same design/behaviour as the /home map. Shows
          every Xaluca hotel, bivouac and refuge so guests can see how
          the addresses spread across the south of Morocco. On /bivouacs
          only the Bivouac filter is on by default — user can toggle any
          other category. */}
      <MapSection initialFilters={{ hotel: false, bivouac: true, refuge: false, landmark: false }} />

      {/* Sticky sub-navigation for this page's sections. Sits just below
          the main site header so guests can jump straight to any block. */}
      <PageStickyNav
        items={[
          { id: "bivouacs-intro", icon: SparkIcon, label: { en: "Intro", fr: "Intro", es: "Intro" } },
          { id: "bivouacs-types", icon: TentIcon, label: { en: "Types", fr: "Types", es: "Tipos" } },
          { id: "bivouacs-atmosphere", icon: CompassIcon, label: { en: "Atmosphere", fr: "Ambiance", es: "Ambiente" } },
          { id: "bivouacs-grid", icon: MapPinIcon, label: { en: "Camps", fr: "Camps", es: "Campamentos" } },
          { id: "bivouacs-cta", icon: MailIcon, label: { en: "Contact", fr: "Contact", es: "Contacto" } },
        ]}
      />

      {/* INTRO — WHAT IS A BIVOUAC */}
      <section id="bivouacs-intro" className="py-20 md:py-28 scroll-mt-32" data-testid="bivouacs-intro">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <p className="overline">{pick(COPY.intro_overline, lang)}</p>
            <h2 className="font-serif-x mt-4 text-4xl md:text-5xl tracking-tight leading-[1.05]">
              {pick(COPY.intro_title, lang)}
            </h2>
          </div>
          <div className="md:col-span-7 flex items-end">
            <p className="text-lg md:text-xl text-[#5C5248] leading-relaxed">
              {pick(COPY.intro_text, lang)}
            </p>
          </div>
        </div>
      </section>

      {/* TYPES OF BIVOUAC */}
      <section id="bivouacs-types" className="bg-[#F2EBE1]/55 py-20 md:py-28 border-y border-[#2C2621]/10 scroll-mt-32" data-testid="bivouacs-types">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-3 gap-10 md:gap-12">
          {[COPY.type_traditional, COPY.type_luxury, COPY.type_stars].map((tp, i) => {
            const Icon = [Tent, BedDouble, Star][i];
            return (
              <div key={`type-${i}`} className="flex flex-col" data-testid={`bivouac-type-${i}`}>
                <div className="w-12 h-12 rounded-full bg-[#1A1513] text-[#D4A373] flex items-center justify-center">
                  <Icon className="w-5 h-5" strokeWidth={1.5} />
                </div>
                <p className="overline mt-6">{`0${i + 1}`}</p>
                <h3 className="font-serif-x text-2xl md:text-3xl mt-2 leading-tight text-[#2C2621]">
                  {pick(tp.title, lang)}
                </h3>
                <p className="mt-5 text-base text-[#5C5248] leading-relaxed">
                  {pick(tp.text, lang)}
                </p>
              </div>
            );
          })}
        </div>
      </section>

      {/* ATMOSPHERE */}
      <section id="bivouacs-atmosphere" className="py-20 md:py-28 scroll-mt-32" data-testid="bivouacs-atmosphere">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <p className="overline">{pick({ en: "Atmosphere", fr: "Ambiance", es: "Ambiente" }, lang)}</p>
            <h2 className="font-serif-x mt-4 text-4xl md:text-5xl tracking-tight leading-[1.05]">
              {pick(COPY.atmosphere_title, lang)}
            </h2>
            <p className="mt-6 text-lg text-[#5C5248] leading-relaxed">
              {pick(COPY.atmosphere_text, lang)}
            </p>
          </div>
          <div className="md:col-span-7 grid grid-cols-2 gap-x-6 gap-y-7">
            {ATMOSPHERE_BEATS.map((b, i) => {
              const Icon = b.icon;
              return (
                <div key={`beat-${i}`} className="flex items-start gap-3" data-testid={`bivouac-atmosphere-${i}`}>
                  <Icon className="w-4 h-4 text-[#C16542] mt-1 shrink-0" strokeWidth={1.5} />
                  <p className="text-base text-[#2C2621] leading-snug">{pick({ en: b.en, fr: b.fr, es: b.es }, lang)}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* DESERT FILMS — bivouac VideoSection */}
      <VideoSection
        overline={{ en: "Films from the dunes", fr: "Films depuis les dunes", es: "Films desde las dunas" }}
        title={{
          en: "A night on the Erg Chebbi, on film.",
          fr: "Une nuit sur l'Erg Chebbi, en images.",
          es: "Una noche en el Erg Chebbi, en imágenes.",
        }}
        intro={{
          en: "Short films from our camps — the haimas at dusk, the Sahrawi musicians, the silence before the stars.",
          fr: "Courts films de nos camps — les haimas au crépuscule, les musiciens sahraouis, le silence avant les étoiles.",
          es: "Cortos de nuestros campamentos — las haimas al crepúsculo, los músicos sahrauis, el silencio antes de las estrellas.",
        }}
        tone="dark"
        featured={{
          id: "bivouacs-featured",
          title: {
            en: "The Sahara, from sunset to first light",
            fr: "Le Sahara, du coucher du soleil aux premières lueurs",
            es: "El Sáhara, del atardecer al alba",
          },
          desc: {
            en: "A guest film capturing the slow ritual of a desert night — fires, music, mint tea, and a sky so dark the Milky Way casts a shadow.",
            fr: "Un film tourné par nos hôtes — feux, musique, thé à la menthe et un ciel si noir que la Voie lactée projette une ombre.",
            es: "Un film de nuestros huéspedes — hogueras, música, té con menta y un cielo tan oscuro que la Vía Láctea proyecta sombras.",
          },
          duration: "3:08",
          tag: { en: "Desert film", fr: "Film du désert", es: "Film del desierto" },
          poster: HERO_IMAGES[0],
        }}
        secondary={[
          {
            id: "bivouacs-haima",
            title: { en: "Inside a haima", fr: "À l'intérieur d'une haima", es: "Dentro de una haima" },
            desc: { en: "How our camel-hair tents are pitched on the sand.", fr: "Comment nos tentes en poil de chameau sont montées sur le sable.", es: "Cómo se montan nuestras tiendas de pelo de camello sobre la arena." },
            duration: "1:42",
            tag: { en: "Craft", fr: "Savoir-faire", es: "Artesanía" },
            poster: "https://images.unsplash.com/photo-1542401886-65d6c61db217?auto=format&fit=crop&w=1600&q=80",
          },
          {
            id: "bivouacs-stars",
            title: { en: "Astronomy with the Sahrawi", fr: "Astronomie avec les Sahraouis", es: "Astronomía con los sahrauis" },
            desc: { en: "Reading Orion, Cassiopeia and the Pleiades from the dunes.", fr: "Lire Orion, Cassiopée et les Pléiades depuis les dunes.", es: "Leer Orión, Casiopea y las Pléyades desde las dunas." },
            duration: "2:21",
            tag: { en: "Sky", fr: "Ciel", es: "Cielo" },
            poster: "https://images.unsplash.com/photo-1532978879514-6cb1cebaaa11?auto=format&fit=crop&w=1600&q=80",
          },
          {
            id: "bivouacs-camel",
            title: { en: "Camel caravan at sunset", fr: "Caravane chamelière au coucher du soleil", es: "Caravana de camellos al atardecer" },
            desc: { en: "Long shadows, copper light, the slow walk to camp.", fr: "Longues ombres, lumière cuivrée, la marche lente vers le camp.", es: "Largas sombras, luz cobriza, el lento paso hacia el campamento." },
            duration: "1:55",
            tag: { en: "Excursion", fr: "Excursion", es: "Excursión" },
            poster: "https://images.unsplash.com/photo-1518684079-3c830dcef090?auto=format&fit=crop&w=1600&q=80",
          },
        ]}
        testid="bivouacs-videos"
      />

      {/* OUR BIVOUACS — DETAILED CARDS */}
      <section id="bivouacs-grid" className="pt-20 md:pt-28 lg:pt-32 pb-24 md:pb-32 scroll-mt-32" data-testid="bivouacs-grid">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="mb-14 md:mb-20">
            <p className="overline">{pick({ en: "Our desert camps", fr: "Nos camps du désert", es: "Nuestros campamentos del desierto" }, lang)}</p>
            <h2 className="font-serif-x mt-4 text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.02] max-w-3xl">
              {pick({
                en: "Three nights, three Saharas",
                fr: "Trois nuits, trois Sahara",
                es: "Tres noches, tres Sáharas",
              }, lang)}
            </h2>
          </div>
          <div className="space-y-14 md:space-y-20">
            {bivouacs.map((b, i) => (
              <BivouacCard key={b.slug} bivouac={b} index={i} lang={lang} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA BAND */}
      <section id="bivouacs-cta" className="bg-[#1A1513] text-[#FDFBF7] py-16 scroll-mt-32" data-testid="bivouacs-cta">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <p className="overline !text-[#D4A373] flex items-center gap-2">
              <Sparkles className="w-3.5 h-3.5" strokeWidth={1.5} />
              {pick({ en: "Plan your night under the stars", fr: "Préparez votre nuit sous les étoiles", es: "Planifique su noche bajo las estrellas" }, lang)}
            </p>
            <h3 className="font-serif-x mt-3 text-3xl md:text-4xl leading-tight max-w-3xl">
              {pick({
                en: "Our concierge will design your bivouac escape",
                fr: "Notre conciergerie compose votre escapade bivouac",
                es: "Nuestro conserje diseña su escapada de vivac",
              }, lang)}
            </h3>
          </div>
          <Link
            to="/contact"
            className="bg-[#C16542] hover:bg-[#A35133] text-[#FDFBF7] px-8 py-4 text-xs tracking-[0.25em] uppercase inline-flex items-center gap-2 w-fit"
            data-testid="bivouacs-cta-contact"
          >
            {pick({ en: "Contact the concierge", fr: "Contacter la conciergerie", es: "Contactar al conserje" }, lang)}
            <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default BivouacsPage;
